import { StyleSheet, css } from 'aphrodite'
import axios from 'axios'
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DOWNSTREAM_PROJECT_IMMUTABLE_FIELDS,
  PROJECT_TEMPLATE_CONFIG,
  type ProjectTemplateRecipientType,
  type SenderStakeholderType,
} from '../../config/templateConfig'
import { useProjectContext } from '../../contexts/ProjectContext'
import { useUser } from '../../contexts/UserContext'
import { useAutoSave } from '../../hooks/useAutoSave'
import { projectService } from '../../services/projectService'
import { userService } from '../../services/userService'
import type { Project } from '../../types/project'
import type { CreateProjectData, UpdateProjectData } from '../../types/project'
import { IntervalType, ProjectStatus } from '../../types/project'
import type { Contact, User } from '../../types/user'
import Modal from '../Modal'
import ProjectHeader from '../ProjectHeader'
import ProjectModalFooter from '../ProjectModalFooter'
import DashboardAddModal from '../dashboard/DashboardAddModal'
import ContactInfoStep from './ContactInfoStep'
import JobDetailsStep from './JobDetailsStep'
import PaymentInfoStep from './PaymentInfoStep'
import ProjectConfirmation from './ProjectConfirmation'
import TemplateContactInfoStep from './TemplateContactInfoStep'

interface AddProjectModalProps {
  onClose: () => void
  project?: Project | null
  readOnly?: boolean
  onSuccess?: () => void
  templateRecipientType?: ProjectTemplateRecipientType | null
}

export const PROJECT_DRAFT_KEY = 'daydaco_project_draft'

const AddProjectModal: React.FC<AddProjectModalProps> = ({
  onClose,
  project,
  readOnly = false,
  onSuccess,
  templateRecipientType,
}) => {
  const navigate = useNavigate()
  const { currentUser } = useUser()
  const { createProject, updateProject, refreshProjects } = useProjectContext()
  const [step, setStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const [contacts, setContacts] = useState<Contact[]>([])
  const [isStepValid, setIsStepValid] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const [draftLoaded, setDraftLoaded] = useState(false)
  // Track the backend draft ID for auto-save updates
  const [backendDraftId, setBackendDraftId] = useState<number | null>(
    project?.status === ProjectStatus.DRAFT ? project.id : null,
  )
  const [submitError, setSubmitError] = useState<string | null>(null)
  // Track pending attachment files before project is created
  const [pendingAttachments, setPendingAttachments] = useState<File[]>([])
  // Track validation state for step 3 (PaymentInfoStep)
  const [isStep3Valid, setIsStep3Valid] = useState(false)
  // Show errors on step 3 when trying to send
  const [showStep3Errors, setShowStep3Errors] = useState(false)
  // Parent project for sub-hauler downstream projects
  const [parentProject, setParentProject] = useState<Project | null>(null)

  // Get user's stakeholder type for determining flow behavior
  const userStakeholderType =
    (currentUser?.current_profile?.stakeholder_type as SenderStakeholderType) ||
    null

  // Determine if this is a sub-contract flow (sub-hauler creating project for another sub-hauler)
  const isSubContract =
    templateRecipientType === 'THIRD_PARTY_SUB_HAULER' ||
    (templateRecipientType === 'SUB_HAULER' &&
      userStakeholderType === 'SUB_HAULER')

  // Determine if this is a broker downstream flow (broker selecting THIRD_PARTY_BROKER template)
  // This is now explicit: THIRD_PARTY_BROKER template always requires parent project
  const isBrokerDownstream = templateRecipientType === 'THIRD_PARTY_BROKER'

  // Combined flag for any downstream flow
  const isDownstreamFlow = isSubContract || isBrokerDownstream

  // Reset error display when step changes (validation is set by step components)
  // biome-ignore lint/correctness/useExhaustiveDependencies: step is intentionally used as a trigger
  useEffect(() => {
    setShowErrors(false)
  }, [step])

  const handleValidationChange = useCallback(
    (isValid: boolean) => {
      console.log(
        'Project validation changed for step',
        step,
        'isValid:',
        isValid,
      )
      setIsStepValid((prev) => (prev !== isValid ? isValid : prev))
    },
    [step],
  )

  // Separate handler for step 3 validation that persists across steps
  const handleStep3ValidationChange = useCallback((isValid: boolean) => {
    console.log('Step 3 (Payment) validation changed:', isValid)
    setIsStep3Valid(isValid)
  }, [])

  // Initialize form data - check localStorage for draft if creating new project
  const getInitialFormData = (): Partial<Project> => {
    // API returns User objects, form stores User objects - no transformation needed
    if (project) return project

    // Try to load draft from localStorage for new projects
    try {
      const savedDraft = localStorage.getItem(PROJECT_DRAFT_KEY)
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft)
        console.log('Loaded project draft from localStorage')
        return parsed
      }
    } catch (error) {
      console.error('Failed to load project draft:', error)
    }

    return {
      date: new Date().toISOString(),
      job_site: '',
      job_id: '',
      starting_date: '',
      completion_date: '',
      pickup_address: '',
      dropoff_address: '',
      material: '',
      rate_type: 'WEIGHT',
      pay_rate: '',
      broker_contacts: [],
      contractor_contacts: [],
      invoice_interval: IntervalType.MONTHLY,
      status_interval: IntervalType.MONTHLY,
      status: ProjectStatus.PENDING,
    }
  }

  const [formData, setFormData] = useState<Partial<Project>>(getInitialFormData)

  // Backend draft ID ref for use in callbacks (avoids stale closure issues)
  const backendDraftIdRef = useRef(backendDraftId)
  useEffect(() => {
    backendDraftIdRef.current = backendDraftId
  }, [backendDraftId])

  // Restore parent project when loading a draft that has parent_project_id
  useEffect(() => {
    const restoreParentProject = async () => {
      // Only restore if we have a parent_project_id but no parentProject loaded yet
      // Works for both sub-hauler and broker downstream flows
      if (formData.parent_project_id && !parentProject && isDownstreamFlow) {
        try {
          console.log(
            'Restoring parent project from draft:',
            formData.parent_project_id,
          )
          const fetchedParent = await projectService.getProject(
            formData.parent_project_id,
          )
          setParentProject(fetchedParent)
          console.log('Parent project restored:', fetchedParent.label)
        } catch (error) {
          console.error('Failed to restore parent project:', error)
        }
      }
    }

    restoreParentProject()
  }, [formData.parent_project_id, parentProject, isDownstreamFlow])

  // Auto-save to backend callback
  const handleAutoSaveDraft = useCallback(
    async (data: Partial<Project>) => {
      // Don't save to backend if no label yet (still loading)
      if (!data.label) {
        console.log('Skipping backend auto-save: no label yet')
        return
      }

      try {
        // Build createData, omitting empty/undefined values
        const createData: CreateProjectData = {
          date: new Date().toISOString(),
          job_site: data.job_site || '',
          job_id: data.job_id || '',
          broker_contacts: data.broker_contacts || [],
          contractor_contacts: data.contractor_contacts || [],
          site_supervisors: data.site_supervisors || [],
          trucks_number: data.trucks_number || '',
          truck_types: data.truck_types || [],
          invoice_interval: data.invoice_interval || IntervalType.MONTHLY,
          material: data.material || '',
          po_number: data.po_number || '',
          rate_type: data.rate_type || '',
          status_interval: data.status_interval || IntervalType.MONTHLY,
          pickup_address: data.pickup_address || '',
          dropoff_address: data.dropoff_address || '',
          notes: data.notes || '',
          status: ProjectStatus.DRAFT,
          // Only include optional fields if they have values
          ...(data.completion_date
            ? { completion_date: data.completion_date }
            : {}),
          ...(data.starting_date ? { starting_date: data.starting_date } : {}),
          ...(data.volume ? { volume: data.volume } : {}),
          ...(data.pay_rate ? { pay_rate: data.pay_rate } : {}),
          ...(templateRecipientType
            ? { template_recipient_type: templateRecipientType }
            : {}),
          ...(data.site_supervisor_first_name
            ? { site_supervisor_first_name: data.site_supervisor_first_name }
            : {}),
          ...(data.site_supervisor_last_name
            ? { site_supervisor_last_name: data.site_supervisor_last_name }
            : {}),
          ...(data.site_supervisor_phone
            ? { site_supervisor_phone: data.site_supervisor_phone }
            : {}),
          ...(data.site_supervisor_email
            ? { site_supervisor_email: data.site_supervisor_email }
            : {}),
          ...(data.contractors?.length
            ? { contractors: data.contractors }
            : {}),
          ...(data.brokers?.length ? { brokers: data.brokers } : {}),
          ...(data.sub_haulers?.length
            ? { sub_haulers: data.sub_haulers }
            : {}),
          ...(data.third_party_sub_haulers?.length
            ? { third_party_sub_haulers: data.third_party_sub_haulers }
            : {}),
          ...(data.third_party_contractors?.length
            ? { third_party_contractors: data.third_party_contractors }
            : {}),
          ...(data.label ? { label: data.label } : {}),
          // Parent project ID for sub-contract flow
          ...(data.parent_project_id
            ? { parent_project_id: data.parent_project_id }
            : {}),
        }

        let savedProject: Project
        const currentDraftId = backendDraftIdRef.current

        if (currentDraftId) {
          // Update existing draft - use projectService directly to avoid refreshing projects list
          console.log('Auto-save: updating existing draft:', currentDraftId)
          savedProject = await projectService.updateProject(
            currentDraftId,
            createData,
          )
        } else {
          // Create new draft - use projectService directly to avoid refreshing projects list
          console.log('Auto-save: creating new draft')
          savedProject = await projectService.saveProject(createData)
          setBackendDraftId(savedProject.id)
        }

        console.log('Auto-save to backend successful:', savedProject.id)
      } catch (error) {
        console.error('Auto-save to backend failed:', error)
        throw error // Let useAutoSave handle the error state
      }
    },
    [templateRecipientType],
  )

  // Use debounced auto-save hook (only for new projects, not editing)
  const {
    status: autoSaveStatus,
    lastSaved: autoSaveLastSaved,
    clearDraft,
  } = useAutoSave({
    storageKey: PROJECT_DRAFT_KEY,
    data: formData,
    enabled: !project && !readOnly,
    debounceMs: 3000, // 3 second debounce as per requirements
    onServerSave: handleAutoSaveDraft,
  })

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const contactsData = await userService.getContacts()
        setContacts(contactsData)
      } catch (error) {
        console.error('Failed to fetch contacts:', error)
      }
    }

    fetchContacts()
  }, [])

  // Initialize site_supervisor_ids when editing an existing project
  useEffect(() => {
    if (project?.site_supervisors && !formData.site_supervisor_ids) {
      const supervisorIds = project.site_supervisors.map((user) =>
        typeof user === 'number' ? user : user.id,
      )
      console.log(
        'Initializing site_supervisor_ids for existing project:',
        supervisorIds,
      )
      setFormData((prev) => ({
        ...prev,
        site_supervisor_ids: supervisorIds,
      }))
    }
  }, [project, formData.site_supervisor_ids])

  // Track if we've already attempted to generate the label
  const labelGenerationAttempted = useRef(false)
  // Store the initial label from draft to avoid regenerating
  const initialLabelRef = useRef(formData.label)

  // Generate project ID and creation date immediately when modal opens (for new projects only)
  useEffect(() => {
    // Only generate once, and only if this is a new project without an existing label
    // Check: already attempted, editing existing project, or draft has a label
    if (
      labelGenerationAttempted.current ||
      project?.label ||
      initialLabelRef.current
    ) {
      return
    }
    labelGenerationAttempted.current = true

    const generateProjectId = async () => {
      try {
        const { label: newLabel, creation_date_formatted } =
          await projectService.generateLabel()
        setFormData((prev) => ({
          ...prev,
          label: newLabel,
          created_date_formatted: creation_date_formatted,
        }))
      } catch (error) {
        console.error('Failed to generate project label on mount:', error)
      }
    }
    generateProjectId()
  }, [project?.label])

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      let savedProject: Project

      /*
       * PROJECT SUBMISSION FLOW:
       * All projects go through a draft → send flow to ensure consistent handling:
       * 1. Auto-save creates a draft in the background (stores backendDraftId)
       * 2. When user clicks "Send", we update the draft with final data, then send it
       * 3. sendDraft() is the single source of truth for sending (handles invitations,
       *    notifications, Stripe billing, etc.)
       * 4. Edge case: if auto-save failed and no draft exists, we create one first, then send
       *
       * This ensures the pre-generated label shown to the user is preserved, and all
       * send-related side effects are handled in one place (the sendDraft endpoint).
       */

      // Check if we have an existing draft (either from props or from auto-save)
      const existingDraftId =
        project?.id && project.status === ProjectStatus.DRAFT
          ? project.id
          : backendDraftId

      // Build the data for create/update
      const projectData: CreateProjectData = {
        date: new Date().toISOString(),
        job_site: formData.job_site || '',
        job_id: formData.job_id || '',
        broker_contacts: formData.broker_contacts || [],
        contractor_contacts: formData.contractor_contacts || [],
        site_supervisors: formData.site_supervisors || [],
        trucks_number: formData.trucks_number || '',
        truck_types: formData.truck_types || [],
        invoice_interval: formData.invoice_interval || IntervalType.MONTHLY,
        material: formData.material || '',
        po_number: formData.po_number || '',
        rate_type: formData.rate_type || '',
        status_interval: formData.status_interval || IntervalType.MONTHLY,
        pickup_address: formData.pickup_address || '',
        dropoff_address: formData.dropoff_address || '',
        notes: formData.notes || '',
        ...(formData.completion_date
          ? { completion_date: formData.completion_date }
          : {}),
        ...(formData.starting_date
          ? { starting_date: formData.starting_date }
          : {}),
        ...(formData.volume ? { volume: formData.volume } : {}),
        ...(formData.pay_rate ? { pay_rate: formData.pay_rate } : {}),
        ...(templateRecipientType
          ? { template_recipient_type: templateRecipientType }
          : {}),
        ...(formData.site_supervisor_first_name
          ? {
              site_supervisor_first_name: formData.site_supervisor_first_name,
            }
          : {}),
        ...(formData.site_supervisor_last_name
          ? { site_supervisor_last_name: formData.site_supervisor_last_name }
          : {}),
        ...(formData.site_supervisor_phone
          ? { site_supervisor_phone: formData.site_supervisor_phone }
          : {}),
        ...(formData.site_supervisor_email
          ? { site_supervisor_email: formData.site_supervisor_email }
          : {}),
        ...(formData.contractors?.length
          ? { contractors: formData.contractors }
          : {}),
        ...(formData.brokers?.length ? { brokers: formData.brokers } : {}),
        ...(formData.sub_haulers?.length
          ? { sub_haulers: formData.sub_haulers }
          : {}),
        ...(formData.third_party_sub_haulers?.length
          ? { third_party_sub_haulers: formData.third_party_sub_haulers }
          : {}),
        ...(formData.third_party_contractors?.length
          ? { third_party_contractors: formData.third_party_contractors }
          : {}),
        ...(formData.label ? { label: formData.label } : {}),
        // Payment schedule fields
        ...(formData.payment_type
          ? { payment_type: formData.payment_type }
          : {}),
        ...(formData.payment_schedule?.length
          ? { payment_schedule: formData.payment_schedule }
          : {}),
        ...(formData.total_scheduled_payment
          ? { total_scheduled_payment: formData.total_scheduled_payment }
          : {}),
        ...(formData.total_scheduled_fees
          ? { total_scheduled_fees: formData.total_scheduled_fees }
          : {}),
        // Parent project ID for sub-contract flow
        ...(formData.parent_project_id
          ? { parent_project_id: formData.parent_project_id }
          : {}),
      }

      if (existingDraftId) {
        // Normal flow: update the existing draft, then send it
        console.log('Sending existing draft:', existingDraftId)
        await updateProject(existingDraftId, {
          ...projectData,
          site_supervisors:
            formData.site_supervisors || project?.site_supervisors || [],
        } as UpdateProjectData)
        savedProject = await projectService.sendDraft(existingDraftId)
      } else if (project?.id) {
        // Updating an existing non-draft project (rare case)
        console.log('Updating existing project:', project.id)
        const updateData: UpdateProjectData = {
          ...formData,
          site_supervisors:
            formData.site_supervisors || project.site_supervisors || [],
        }
        savedProject = await updateProject(project.id, updateData)
      } else {
        // Edge case: no draft exists (auto-save may have failed)
        // Create a draft first, then send it to maintain consistent flow
        console.log(
          'No existing draft found, creating draft then sending:',
          projectData,
        )
        const draft = await createProject({
          ...projectData,
          status: ProjectStatus.DRAFT,
        })
        savedProject = await projectService.sendDraft(draft.id)
      }

      console.log('Project saved successfully:', savedProject)

      // Upload pending attachments if any
      if (pendingAttachments.length > 0) {
        try {
          await projectService.uploadAttachments(
            Number(savedProject.id),
            pendingAttachments,
          )
          console.log('Attachments uploaded successfully')
        } catch (attachmentError) {
          // Log the error but don't fail the project creation
          console.error('Failed to upload attachments:', attachmentError)
          // Optionally notify user that attachments failed but project was created
        }
      }

      // Clear draft from localStorage after successful save
      clearDraft()

      if (formData.pendingInvites?.length) {
        console.log('Sending invites to:', formData.pendingInvites)
        await Promise.all(
          formData.pendingInvites.map((email: string) =>
            projectService.createProjectInvite(Number(savedProject.id), {
              email,
            }),
          ),
        )
      }

      onClose()
      // Navigate to pending tab after save
      navigate('/projects/pending')
      refreshProjects()
      if (onSuccess) onSuccess()
    } catch (error: unknown) {
      console.error('Failed to save project:', error)
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to send project terms. Please try again.'
      setSubmitError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSaveDraft = async () => {
    setIsSavingDraft(true)
    setSubmitError(null)
    try {
      // Build createData, omitting empty/undefined values
      const createData: CreateProjectData = {
        date: new Date().toISOString(),
        job_site: formData.job_site || '',
        job_id: formData.job_id || '',
        broker_contacts: formData.broker_contacts || [],
        contractor_contacts: formData.contractor_contacts || [],
        site_supervisors: formData.site_supervisors || [],
        trucks_number: formData.trucks_number || '',
        truck_types: formData.truck_types || [],
        invoice_interval: formData.invoice_interval || IntervalType.MONTHLY,
        material: formData.material || '',
        po_number: formData.po_number || '',
        rate_type: formData.rate_type || '',
        status_interval: formData.status_interval || IntervalType.MONTHLY,
        pickup_address: formData.pickup_address || '',
        dropoff_address: formData.dropoff_address || '',
        notes: formData.notes || '',
        status: ProjectStatus.DRAFT,
        // Only include optional fields if they have values
        ...(formData.completion_date
          ? { completion_date: formData.completion_date }
          : {}),
        ...(formData.starting_date
          ? { starting_date: formData.starting_date }
          : {}),
        ...(formData.volume ? { volume: formData.volume } : {}),
        ...(formData.pay_rate ? { pay_rate: formData.pay_rate } : {}),
        ...(templateRecipientType
          ? { template_recipient_type: templateRecipientType }
          : {}),
        ...(formData.site_supervisor_first_name
          ? { site_supervisor_first_name: formData.site_supervisor_first_name }
          : {}),
        ...(formData.site_supervisor_last_name
          ? { site_supervisor_last_name: formData.site_supervisor_last_name }
          : {}),
        ...(formData.site_supervisor_phone
          ? { site_supervisor_phone: formData.site_supervisor_phone }
          : {}),
        ...(formData.site_supervisor_email
          ? { site_supervisor_email: formData.site_supervisor_email }
          : {}),
        ...(formData.contractors?.length
          ? { contractors: formData.contractors }
          : {}),
        ...(formData.brokers?.length ? { brokers: formData.brokers } : {}),
        ...(formData.sub_haulers?.length
          ? { sub_haulers: formData.sub_haulers }
          : {}),
        ...(formData.third_party_sub_haulers?.length
          ? { third_party_sub_haulers: formData.third_party_sub_haulers }
          : {}),
        ...(formData.third_party_contractors?.length
          ? { third_party_contractors: formData.third_party_contractors }
          : {}),
        ...(formData.label ? { label: formData.label } : {}),
        // Payment schedule fields
        ...(formData.payment_type
          ? { payment_type: formData.payment_type }
          : {}),
        ...(formData.payment_schedule?.length
          ? { payment_schedule: formData.payment_schedule }
          : {}),
        ...(formData.total_scheduled_payment
          ? { total_scheduled_payment: formData.total_scheduled_payment }
          : {}),
        ...(formData.total_scheduled_fees
          ? { total_scheduled_fees: formData.total_scheduled_fees }
          : {}),
        // Parent project ID for sub-contract flow
        ...(formData.parent_project_id
          ? { parent_project_id: formData.parent_project_id }
          : {}),
      }

      let savedProject: Project

      // Check if we have an existing draft (either from props or from auto-save)
      const existingDraftId =
        project?.id && project.status === ProjectStatus.DRAFT
          ? project.id
          : backendDraftId

      if (existingDraftId) {
        // Update existing draft
        console.log('Updating existing draft:', existingDraftId)
        savedProject = await updateProject(existingDraftId, createData)
      } else {
        // Create new draft
        console.log('Creating new draft with data:', createData)
        savedProject = await createProject(createData)
      }

      console.log('Draft saved successfully:', savedProject)

      // Upload pending attachments if any
      if (pendingAttachments.length > 0) {
        try {
          await projectService.uploadAttachments(
            Number(savedProject.id),
            pendingAttachments,
          )
          console.log('Attachments uploaded successfully')
        } catch (attachmentError) {
          // Log the error but don't fail the draft save
          console.error('Failed to upload attachments:', attachmentError)
        }
      }

      // Clear draft from localStorage after successful save to backend
      clearDraft()

      onClose()
      // Navigate to drafts tab
      navigate('/projects/drafts')
      refreshProjects()
      if (onSuccess) onSuccess()
    } catch (error: unknown) {
      console.error('Failed to save draft:', error)
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to save draft. Please try again.'
      setSubmitError(errorMessage)
    } finally {
      setIsSavingDraft(false)
    }
  }

  const handleNext = async () => {
    console.log(
      'Project handleNext called - step:',
      step,
      'isStepValid:',
      isStepValid,
    )

    // Check validation for steps 1 and 2
    if ((step === 1 || step === 2) && !isStepValid) {
      console.log('Project validation failed, not proceeding to next step')
      setShowErrors(true)
      return // Don't proceed if validation fails
    }

    // Check validation for step 3 (Payment Info)
    if (step === 3 && !isStep3Valid) {
      console.log(
        'Project step 3 validation failed, not proceeding to next step',
      )
      setShowStep3Errors(true)
      return // Don't proceed if validation fails
    }

    if (step < 4) {
      setStep(step + 1)
    } else {
      handleSubmit()
    }
  }

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1)
    }
  }

  const updateFormData = useCallback((data: Partial<Project>) => {
    setFormData((prev) => {
      // Shallow compare only the keys provided in data to prevent needless updates
      let changed = false
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          const k = key as keyof Project
          if (prev[k] !== data[k]) {
            changed = true
            break
          }
        }
      }
      if (!changed) return prev
      return { ...prev, ...data }
    })
  }, [])

  // Handle parent project selection for sub-contract flow
  const handleParentProjectSelect = useCallback(
    (selectedParent: Project | null) => {
      setParentProject(selectedParent)

      if (selectedParent) {
        // Pre-populate fields from parent project
        // Use empty string/array as fallback to ensure values are serializable
        const inheritedData: Partial<Project> = {
          parent_project_id: selectedParent.id,
          // Copy fields from parent (with fallbacks for undefined values)
          material: selectedParent.material || '',
          pickup_address: selectedParent.pickup_address || '',
          dropoff_address: selectedParent.dropoff_address || '',
          job_site: selectedParent.job_site || '',
          job_id: selectedParent.job_id || '',
          po_number: selectedParent.po_number || '',
          truck_types: selectedParent.truck_types || [],
          third_party_location: selectedParent.third_party_location || '',
        }
        setFormData((prev) => ({ ...prev, ...inheritedData }))
      } else {
        // Clear inherited data when parent is cleared
        setFormData((prev) => {
          const cleared = { ...prev }
          cleared.parent_project_id = undefined
          // Clear immutable fields
          for (const field of DOWNSTREAM_PROJECT_IMMUTABLE_FIELDS) {
            ;(cleared as Record<string, unknown>)[field] = ''
          }
          return cleared
        })
      }
    },
    [],
  )

  const renderStep = () => {
    const commonProps = {
      onUpdate: updateFormData,
      data: formData,
      readOnly: readOnly,
    }

    switch (step) {
      case 1:
        // Use TemplateContactInfoStep when a template is selected
        // Key ensures component identity is preserved across re-renders
        if (templateRecipientType) {
          return (
            <TemplateContactInfoStep
              key="template-contact-info"
              {...commonProps}
              onValidationChange={handleValidationChange}
              showErrors={showErrors}
              templateType={templateRecipientType}
              isSubContract={isSubContract}
              isBrokerDownstream={isBrokerDownstream}
              parentProject={parentProject}
              onParentProjectSelect={handleParentProjectSelect}
              userStakeholderType={userStakeholderType}
            />
          )
        }
        return (
          <ContactInfoStep
            key="contact-info"
            {...commonProps}
            onValidationChange={handleValidationChange}
            showErrors={showErrors}
          />
        )
      case 2:
        return (
          <JobDetailsStep
            {...commonProps}
            onValidationChange={handleValidationChange}
            showErrors={showErrors}
            projectId={project?.id}
            pendingAttachments={pendingAttachments}
            onPendingAttachmentsChange={setPendingAttachments}
            onAttachmentsChange={(attachments) =>
              setFormData((prev) => ({ ...prev, attachments }))
            }
            isSubContract={isDownstreamFlow}
            parentProject={parentProject}
          />
        )
      case 3:
        return (
          <PaymentInfoStep
            {...commonProps}
            onValidationChange={handleStep3ValidationChange}
            showErrors={showStep3Errors}
          />
        )
      case 4:
        return (
          <ProjectConfirmation
            data={formData}
            contacts={contacts}
            creator={currentUser as User | null}
            useDirectData={true}
            templateRecipientType={templateRecipientType}
            pendingAttachments={pendingAttachments.map((f) => ({
              name: f.name,
              size: f.size,
              type: f.type,
            }))}
            showLiveSentDate={true}
            parentProject={parentProject}
            senderStakeholderType={userStakeholderType}
          />
        )
      default:
        return null
    }
  }

  // Show Save Draft button for new projects or when editing drafts
  const showSaveDraft = !project?.id || project?.status === ProjectStatus.DRAFT

  // Get modal title from template config or use default
  const modalTitle = templateRecipientType
    ? PROJECT_TEMPLATE_CONFIG[templateRecipientType].title
    : 'Project Terms'

  // Check if the form is fully valid (for Send button on step 4)
  // We need payment step (step 3) to be valid for the form to be complete
  const isFormValid = isStep3Valid

  // Compute validation error for footer display
  const getValidationError = (): string | null => {
    if (!showErrors) return null

    if (step === 2) {
      // Job Details validation
      const requiredFields = [
        { value: formData.starting_date, name: 'Starting Date' },
        { value: formData.pickup_address, name: 'Pick Up Location' },
        { value: formData.dropoff_address, name: 'Drop Off Location' },
      ]
      const missingFields = requiredFields.filter(
        (field) => !field.value || field.value.trim() === '',
      )
      if (missingFields.length > 0) {
        return `Please fill in all required fields: ${missingFields.map((f) => f.name).join(', ')}`
      }
    }

    return null
  }

  return (
    <DashboardAddModal
      onClose={onClose}
      onSubmit={handleSubmit}
      onNext={handleNext}
      onBack={handleBack}
      title={modalTitle}
      projectId={project?.label || formData.label}
      poNumber={project?.id ? formData.po_number : undefined}
      step={step}
      totalSteps={4}
      readOnly={readOnly}
      isSubmitting={isSubmitting}
      isNextDisabled={step === 4 && !isFormValid}
      onSaveDraft={handleSaveDraft}
      showSaveDraft={showSaveDraft}
      isSavingDraft={isSavingDraft}
      onClearDraft={clearDraft}
      validationError={getValidationError()}
      submitError={submitError}
      onDismissSubmitError={() => setSubmitError(null)}
      autoSaveStatus={!project && !readOnly ? autoSaveStatus : undefined}
      autoSaveLastSaved={!project && !readOnly ? autoSaveLastSaved : undefined}
      onTrySubmitWithErrors={
        step === 4 && !isFormValid ? () => setShowStep3Errors(true) : undefined
      }
    >
      {renderStep()}
    </DashboardAddModal>
  )
}

const styles = StyleSheet.create({
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '600px',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
  },
  content: {
    padding: '24px',
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '16px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
  },
  addSubhaulerBtn: {
    color: '#1C64F2',
    backgroundColor: 'transparent',
    border: 'none',
    padding: '8px 0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  },
  inviteSection: {
    marginTop: '24px',
  },
  inviteForm: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
  inviteInput: {
    flex: 1,
    padding: '8px 12px',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    fontSize: '14px',
  },
  roleSelectWrapper: {
    width: '120px',
  },
  inviteButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    cursor: 'pointer',
  },
  copyLinkBtn: {
    color: '#1C64F2',
    backgroundColor: 'transparent',
    border: 'none',
    padding: '8px 0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  },
})

export default AddProjectModal
